import { LoadingSpinner } from "@connected-repo/ui-mui/components/LoadingSpinner";
import { Box } from "@connected-repo/ui-mui/layout/Box";
import type { MyStand } from "@connected-repo/zod-schemas/room_session.zod";
import { useWorkspace } from "@frontend/contexts/WorkspaceContext";
import { useTenant } from "@frontend/tenant/TenantContext";
import { orpc } from "@frontend/utils/orpc.tanstack.client";
import { useQuery } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useRef } from "react";
import { Outlet, Link as RouterLink, useParams } from "react-router";
import { EXHIBITOR } from "./components/room_palette";

interface StandRoute {
	eventId: string;
	/** `/stands/<eventId>`: every page of the stand hangs off it. */
	base: string;
	stand: MyStand;
}

const StandContext = createContext<StandRoute | null>(null);

/** The stand the URL names; only inside `StandGate`. */
export function useStandRoute(): StandRoute {
	const value = useContext(StandContext);
	if (!value) throw new Error("useStandRoute must be used under StandGate");
	return value;
}

/** The person's stands this host may open: one team's on its subdomain, every team's on the shared app. */
export function useMyStands() {
	const { slug } = useTenant();
	const query = useQuery({ ...orpc.stands.mine.queryOptions(), staleTime: 30_000 });
	const stands = (query.data ?? []).filter((s) => !slug || s.teamSlug === slug);
	return { ...query, stands };
}

const Centered = ({ children }: { children: React.ReactNode }) => (
	<Box
		sx={{
			minHeight: "100dvh",
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
			gap: 2,
			p: 3,
			textAlign: "center",
			fontFamily: "Inter, system-ui, sans-serif",
		}}
	>
		{children}
	</Box>
);

/**
 * `/stands/:eventId/*` (root ADR-036): the stand the URL names, read from the
 * person's stands on every team. A stand on another team than the active one
 * switches the session to that team first — the exhibitor procedures are
 * scoped to the active team — and the pages render once it has.
 */
export function StandGate() {
	const { eventId = "" } = useParams<{ eventId: string }>();
	const { activeWorkspace, setActiveTeam, isSwitching } = useWorkspace();
	const { stands, isLoading, isError } = useMyStands();
	const stand = stands.find((s) => s.eventId === eventId);
	const asked = useRef<string | null>(null);

	const elsewhere = stand !== undefined && stand.teamId !== activeWorkspace.id;
	useEffect(() => {
		if (!stand || !elsewhere || asked.current === stand.teamId) return;
		asked.current = stand.teamId;
		void setActiveTeam(stand.teamId).catch(() => {
			asked.current = null;
		});
	}, [stand, elsewhere, setActiveTeam]);

	if (isLoading || (elsewhere && (isSwitching || asked.current === stand?.teamId))) {
		return (
			<Centered>
				<LoadingSpinner />
			</Centered>
		);
	}
	if (isError || !stand) {
		return (
			<Centered>
				<Box component="h1" sx={{ m: 0, fontSize: 22, fontWeight: 600, color: EXHIBITOR.ink }}>
					No stand of yours at this event
				</Box>
				<Box component="p" sx={{ m: 0, color: "#5b6075", maxWidth: 360 }}>
					The organiser may not have added you yet, or this link is for another event. Your
					stands are on your home page.
				</Box>
				<Box
					component={RouterLink}
					to="/dashboard"
					sx={{ height: 48, px: 3, display: "inline-flex", alignItems: "center", borderRadius: "12px", bgcolor: EXHIBITOR.main, color: "#fff", font: "600 15px Inter, sans-serif", textDecoration: "none" }}
				>
					Your stands
				</Box>
			</Centered>
		);
	}
	return (
		<StandContext.Provider value={{ eventId, base: `/stands/${eventId}`, stand }}>
			<Outlet />
		</StandContext.Provider>
	);
}
